/* eslint-disable prettier/prettier */
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { receiverTypes } from '../../helpers/Constants'
import { VARIABLE_IN_TEXT, inChannelStyle } from '../../helpers/templateForm'
import useAiStatus from '../../hooks/useAiStatus'
import AIService from '../../services/aiService'
import { errorMessage } from '../../services/axios/errorMessage'
import { BRAND, ICON, RADIUS, SURFACE, TEXT, cardSx, menuProps } from '../../theme/tokens'

/** The channels the AI Suite's template generator writes for. */
const AI_CHANNELS = ['email', 'whatsapp', 'sms', 'push_notification', 'slack', 'voice']

const SUGGESTIONS = ['Make it shorter', 'Warmer tone', 'Add a call to action']

const MAX_HISTORY = 6

/** The variables the AI may use: the form's, then any written in the message. */
const allowedVariables = (listed, message) => [
    ...new Set([
        ...listed,
        ...[...message.matchAll(VARIABLE_IN_TEXT)].map((m) => m[1] || m[2]),
    ]),
]

const Row = ({ role, children }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
            gap: 1,
        }}
    >
        {role !== 'user' && (
            <AutoAwesomeIcon
                sx={{ fontSize: ICON.md, color: BRAND.orange, mt: '3px', flexShrink: 0 }}
            />
        )}
        <Typography
            variant="body2"
            sx={{
                maxWidth: '85%',
                color: role === 'user' ? TEXT.primary : TEXT.secondary,
                ...(role === 'user' && {
                    background: SURFACE.sunken,
                    borderRadius: `${RADIUS.md}px`,
                    px: 1.5,
                    py: 0.75,
                }),
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
        >
            {children}
        </Typography>
    </Box>
)

Row.propTypes = {
    role: PropTypes.oneOf(['user', 'assistant']).isRequired,
    children: PropTypes.node,
}

/**
 * The AI Suite's template generator as a slim chat above the content of Create
 * Template: optional, closed to begin with (one row), so the form can be filled
 * by hand as ever. On an empty form the first message describes the template
 * and the AI fills the form; once the form has a draft (the AI's, a hand-off's,
 * or text typed by hand) a message asks for a change to it ("make it shorter")
 * and the AI answers the whole updated template, which replaces the draft.
 *
 * It writes for the form's own channel (every channel the form has), so changing
 * the channel starts a new chat (the parent keys it by channel).
 *
 * @param {string} channel  the form's channel
 * @param {() => object|null} getCurrent  the draft to change: { subject, body, content, buttons }
 * @param {(result: object, info: {first: boolean}) => void} onApply  fills the form from the AI's answer
 * @param {boolean} handEdited  the email's blocks were changed by hand since the AI's last draft
 * @param {string[]} variables  the form's Variables list, which the AI uses
 * @param {string} unavailable  why the chat is off for now (the chat is kept for when it is back)
 */
const AiAssistant = ({
    channel,
    getCurrent,
    onApply,
    handEdited = false,
    variables = [],
    unavailable = '',
}) => {
    const ai = useAiStatus()
    const [open, setOpen] = useState(false)
    const [messages, setMessages] = useState([])
    const [input, setInput] = useState('')
    const [receiverType, setReceiverType] = useState('batch_student')
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')
    const mounted = useRef(true)
    // Set at once on Send (state is not yet updated on a quick second click or Enter).
    const inFlight = useRef(false)
    const scroller = useRef(null)
    const written = messages.some((m) => m.role === 'assistant')
    // A draft is in the form (an answer, a hand-off, text typed by hand): a message changes it.
    const formDraft = getCurrent()
    const hasDraft = Boolean(
        formDraft && (String(formDraft.body ?? '').trim() || formDraft.content)
    )

    useEffect(
        () => () => {
            mounted.current = false
        },
        []
    )
    useEffect(() => {
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight
    }, [messages])

    const supported = AI_CHANNELS.includes(channel)
    let off = ''
    if (!supported) off = 'AI writing does not cover this channel.'
    else if (unavailable) off = unavailable
    else if (ai.state === 'checking') off = 'Checking that the AI model is available…'
    else if (ai.state !== 'ready')
        off = 'The AI model is not available right now. You can still fill the template in by hand.'

    const send = async (text) => {
        const message = String(text ?? input).trim()
        if (!message || inFlight.current || off) return
        inFlight.current = true
        const first = !written
        const asked = messages.filter((m) => m.role === 'user').map((m) => m.text)
        setSending(true)
        setError('')
        setInput('')
        setMessages((all) => [...all, { role: 'user', text: message }])
        try {
            // A draft already in the form (an earlier answer, a hand-off, text
            // typed by hand) is changed; an empty form is written afresh.
            const draft = getCurrent()
            const current =
                draft && (String(draft.body ?? '').trim() || draft.content) ? draft : null
            const res = await AIService.generateTemplate({
                // On WhatsApp the AI read "use ${amount}" as a dollar sign.
                description: inChannelStyle(message, channel),
                channel,
                receiver_type: receiverType,
                variables: allowedVariables(variables, message),
                // The chat may add a variable the message needs (a join link);
                // the form lists every variable it finds, so the user sees it.
                allow_new_variables: true,
                ...(current ? { current, history: asked.slice(-MAX_HISTORY) } : {}),
            }, { silentErrorToast: true })
            if (!mounted.current) return
            const result = res.data?.result
            if (!result?.body) {
                setError('The AI returned no template. Please try again.')
                setInput(message)
                return
            }
            // The same template back is not an update: say so, with the reason.
            const unchanged =
                current &&
                (current.content
                    ? JSON.stringify(result.content) === JSON.stringify(current.content) &&
                      (result.subject || '') === (current.subject || '')
                    : String(result.body).trim() === String(current.body ?? '').trim() &&
                      (result.subject || '') === (current.subject || '') &&
                      (!Array.isArray(result.buttons) ||
                          JSON.stringify(result.buttons) === JSON.stringify(current.buttons ?? [])))
            onApply(result, { first })
            const said =
                result.note ||
                (current
                    ? 'Updated the draft in the form below.'
                    : 'The draft is in the form below.')
            setMessages((all) => [
                ...all,
                {
                    role: 'assistant',
                    text: unchanged ? `Nothing changed: ${result.note || 'the AI gave back the same template.'}` : said,
                },
            ])
        } catch (err) {
            if (!mounted.current) return
            setError(`${errorMessage(err?.response)} Try again.`)
            setInput(message)
        } finally {
            inFlight.current = false
            if (mounted.current) setSending(false)
        }
    }

    return (
        <Box
            sx={{
                ...cardSx,
                px: 2,
                py: 1.25,
                mb: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AutoAwesomeIcon sx={{ fontSize: ICON.lg, color: BRAND.orange }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" component="h2">
                        Write this template with AI
                        <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: TEXT.muted, ml: 1 }}
                        >
                            optional
                        </Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ color: TEXT.muted }}>
                        {off ||
                            'Describe it, then ask for changes. Or fill the template in by hand.'}
                    </Typography>
                </Box>
                {open && !off ? (
                    <IconButton
                        size="small"
                        aria-label="Close the AI chat"
                        onClick={() => setOpen(false)}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                ) : (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={Boolean(off)}
                        onClick={() => setOpen(true)}
                    >
                        {written ? 'Open chat' : 'Start'}
                    </Button>
                )}
            </Box>

            {open && !off && (
                <>
                    {messages.length > 0 && (
                        // Only the messages scroll, so the box changes height once
                        // per message. The gutter is kept, so text does not reflow
                        // when the bar appears, and the bar is thin and themed.
                        <Box
                            ref={scroller}
                            aria-label="AI chat"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                maxHeight: 220,
                                overflowY: 'auto',
                                scrollbarGutter: 'stable',
                                scrollbarWidth: 'thin',
                                scrollbarColor: `${SURFACE.borderHover} transparent`,
                                '&::-webkit-scrollbar': { width: 6 },
                                '&::-webkit-scrollbar-thumb': {
                                    background: SURFACE.borderHover,
                                    borderRadius: `${RADIUS.sm}px`,
                                },
                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                pr: 0.5,
                            }}
                        >
                            {messages.map((m, i) => (
                                <Row key={i} role={m.role}>
                                    {m.text}
                                </Row>
                            ))}
                        </Box>
                    )}

                    {/* One slot under the messages: writing, the failure, or the
                        suggestions; it keeps its place while a message is sent. */}
                    {(sending || error || written) && (
                        <Box sx={{ minHeight: 28, display: 'flex', alignItems: 'center' }}>
                            {sending ? (
                                <Box
                                    role="status"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <CircularProgress size={14} sx={{ color: BRAND.orange }} />
                                    <Typography variant="caption" sx={{ color: TEXT.muted }}>
                                        Writing…
                                    </Typography>
                                </Box>
                            ) : error ? (
                                <Box sx={{ flex: 1 }}>
                                    <ErrorBanner message={error} />
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                    {SUGGESTIONS.map((s) => (
                                        <Chip
                                            key={s}
                                            size="small"
                                            variant="outlined"
                                            label={s}
                                            onClick={() => send(s)}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Select
                            size="small"
                            value={receiverType}
                            onChange={(e) => setReceiverType(e.target.value)}
                            MenuProps={menuProps}
                            inputProps={{ 'aria-label': 'Receiver type' }}
                            sx={{ minWidth: 170 }}
                        >
                            {receiverTypes.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    {r.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" sx={{ color: TEXT.muted }}>
                            Uses the Variables listed in the form, and any you write as{' '}
                            {channel === 'whatsapp' ? '{{name}}' : '${name}'}.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            maxRows={4}
                            placeholder={
                                written || hasDraft
                                    ? 'Ask for a change, e.g. make it shorter'
                                    : 'Describe the message, e.g. remind a batch student 2 hours before their Maths class'
                            }
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    send()
                                }
                            }}
                            inputProps={{ 'aria-label': 'Message to the AI' }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            endIcon={<SendIcon />}
                            disabled={!input.trim() || sending}
                            onClick={() => send()}
                        >
                            Send
                        </Button>
                    </Box>
                    {written && handEdited && (
                        <Typography variant="caption" sx={{ color: TEXT.muted }}>
                            You changed the blocks by hand. A change asked for here is made
                            to the last AI draft and replaces your edits.
                        </Typography>
                    )}
                </>
            )}
        </Box>
    )
}

AiAssistant.propTypes = {
    channel: PropTypes.string.isRequired,
    getCurrent: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    handEdited: PropTypes.bool,
    variables: PropTypes.arrayOf(PropTypes.string),
    unavailable: PropTypes.string,
}

export default AiAssistant
